import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    Tooltip,
    styled,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    createTheme,
    ThemeProvider,
    CssBaseline,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    GlobalStyles,
    Collapse,
    Alert,
    Snackbar,
    Chip,
    Stack,
    alpha,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    List,
    ListItem,
    ListItemText,
    Divider,
    Input, // Added for Color input in Confirm Dialog
    Grid,
    Autocomplete // Mantido no import, embora não usado no modal principal
} from '@mui/material';
import {
    Building2,
    Users,
    Trash2,
    X as XLucide,
    // Save, // Removed as unused
    Wand2,
    FilePenLine,
    AlertTriangle,
    CheckCircle2,
    Plus,
    UserCheck,
    CloudCheck,
    RefreshCw,
    Briefcase,
    Puzzle,
    Shapes, // Icon for Category
    ShieldCheck,
    Upload,
    Building
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { SketchPicker } from 'react-color'; // For CreateAreaDialog
import { keyframes } from '@mui/system';
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
import Papa from 'papaparse';
import { UploadFile } from '@mui/icons-material';

// --- ICONS AND HELPER COMPONENTS ---
const HelpCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>;
const XIcon = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>;
// CORREÇÃO: Removido 'border: none' duplicado que causava aviso
const ghostButtonStyle = { padding: '10px 18px', fontSize: '14px', cursor: 'pointer', borderRadius: '8px', fontWeight: '500', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: 'transparent', color: '#334155', border: '1px solid #cbd5e1' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 10000, backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '8vh 24px 5vh 24px' };
const confirmationModalOverlayStyle = { ...modalOverlayStyle, zIndex: 10200 }; // Ensure confirmation is on top

// --- EXPLANATORY VIDEO MODAL (Unchanged) ---
const ExplanatoryVideoModal = ({ isOpen, onClose }) => {
    const [activeVideoId, setActiveVideoId] = useState(null);
    const videoTopics = [ { title: "O que são Competências Corporativas?", videoId: "SEU_ID_DO_YOUTUBE_AQUI_1" }, { title: "O que são Competências Gerenciais?", videoId: "SEU_ID_DO_YOUTUBE_AQUI_2" }, { title: "Como escrever uma boa descrição de competência?", videoId: "SEU_ID_DO_YOUTUBE_AQUI_3" }, ];
    useEffect(() => { if (!isOpen) { setTimeout(() => { setActiveVideoId(null); }, 300); } }, [isOpen]);
    if (!isOpen) return null;
    const videoModalContentStyle = { background: 'white', padding: '32px', borderRadius: '12px', width: '90%', maxWidth: activeVideoId ? '960px' : '600px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', transition: 'all 0.3s ease-in-out', position: 'relative' };
    const iframeContainerStyle = { position: 'relative', paddingTop: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px', backgroundColor: '#000' };
    const iframeStyle = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' };
    const topicItemStyle = { padding: '16px 20px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s ease', fontWeight: '500', color: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
    const TopicItem = ({ title, onClick }) => { const [hover, setHover] = useState(false); return ( <div style={{ ...topicItemStyle, backgroundColor: hover ? '#f8fafc' : '#fff', transform: hover ? 'translateY(-2px)' : 'none', boxShadow: hover ? '0 4px 10px rgba(0,0,0,0.05)' : 'none' }} onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}> {title} <span style={{ transition: 'transform 0.2s', transform: hover ? 'translateX(5px)' : 'none' }}>→</span> </div> ); };
    return ( <div style={modalOverlayStyle} onClick={onClose}> <div style={videoModalContentStyle} onClick={(e) => e.stopPropagation()}> <button style={{ ...ghostButtonStyle, position: 'absolute', top: '16px', right: '16px', padding: '8px', zIndex: 20, border: 'none' }} onClick={onClose}><XIcon /></button> {!activeVideoId ? ( <> <h2 style={{ marginTop: 0, color: '#1e293b' }}>Vídeos Explicativos</h2> <p style={{ color: '#475569', lineHeight: '1.6', marginTop: 0, marginBottom: '24px' }}>Selecione um tópico abaixo para assistir ao vídeo correspondente.</p> <div> {videoTopics.map((topic, index) => ( <TopicItem key={index} title={topic.title} onClick={() => setActiveVideoId(topic.videoId)} /> ))} </div> </> ) : ( <> <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '12px' }}> <button style={{ ...ghostButtonStyle, padding: '8px', border: 'none' }} onClick={() => setActiveVideoId(null)}><BackIcon /></button> <h3 style={{ margin: 0, color: '#1e293b' }}>{videoTopics.find(t => t.videoId === activeVideoId)?.title}</h3> </div> <div style={iframeContainerStyle}> <iframe style={iframeStyle} src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe> </div> </> )} </div> </div> );
};

// --- THEME AND STYLES (Unchanged) ---
const theme = createTheme({
    palette: { primary: { main: '#2563eb', light: '#dbeafe', dark: '#1e40af' }, secondary: { main: '#475569' }, managerial: { main: '#0d9488', light: '#ccfbf1' }, technical: { main: '#7c3aed', light: '#ede9fe' }, other: { main: '#db2777', light: '#fce7f3' }, background: { default: '#f8fafc', paper: '#ffffff' }, text: { primary: '#1e293b', secondary: '#64748b' }, success: { main: '#16a34a' }, warning: { main: '#f97316', light: '#ffedd5' }, error: { main: '#dc2626' }, },
    typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", h1: { fontWeight: 700 }, h2: { fontWeight: 700 }, h3: { fontWeight: 600 }, h4: { fontWeight: 600 }, h5: { fontWeight: 600 }, button: { textTransform: 'none', fontWeight: 600 }, },
    shape: { borderRadius: 12 },
});
const fadeInUp = keyframes`from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); }`;
const PageWrapper = styled(Box)({ width: '100%', minHeight: '100vh', backgroundColor: theme.palette.background.default, padding: theme.spacing(4, 2) });
const HeaderSection = styled(Box)(({ theme }) => ({ textAlign: 'center', marginBottom: theme.spacing(6), animation: `${fadeInUp} 0.7s ease-out` }));
const BaseTabSelector = styled(ToggleButtonGroup)(({ theme }) => ({ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.grey[200]}`, padding: theme.spacing(0.5), borderRadius: theme.shape.borderRadius, '& .MuiToggleButton-root': { margin: 0, border: 0, color: theme.palette.text.secondary, padding: theme.spacing(1, 2.5), transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out', '&.Mui-selected': { fontWeight: 600 }, }, }));
const CardsGrid = styled(Box)(({ theme }) => ({ display: 'grid', gap: theme.spacing(3), gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', alignItems: 'stretch' }));
const BaseCard = styled(Paper)(({ theme }) => ({ position: 'relative', width: '100%', height: '100%', padding: theme.spacing(2.5), backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.grey[200]}`, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', transition: 'transform 0.25s ease, box-shadow 0.25s ease', display: 'flex', flexDirection: 'column', textAlign: 'left', overflow: 'hidden' }));
const CompetencyCard = styled(BaseCard)(({ theme, index, tipo }) => { const typeColors = { corporativas: theme.palette.primary, gerenciais: theme.palette.managerial, tecnicas: theme.palette.technical, outras: theme.palette.other, }; const color = typeColors[tipo] || theme.palette.primary; return { animation: `${fadeInUp} ${0.45 + (index || 0) * 0.06}s ease-out forwards`, opacity: 0, '&:hover': { transform: 'translateY(-6px)', borderColor: color.main, boxShadow: '0 12px 20px rgba(0,0,0,0.08)' } }; });
const AddCard = styled(BaseCard)(({ theme }) => ({ alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: `2px dashed ${theme.palette.grey[300]}`, color: theme.palette.text.secondary, boxShadow: 'none', '&:hover': { color: theme.palette.primary.main, borderColor: theme.palette.primary.main, backgroundColor: theme.palette.primary.light } }));
const CardIconWrapper = styled(Box)({ borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });
const getIconSize = (nome = '') => { const len = nome.length; if (len > 40) return 18; if (len > 28) return 20; if (len > 18) return 24; return 28; };

// --- AUTOSAVE FEEDBACK (Unchanged) ---
const AutoSaveFeedback = ({ status }) => { if (status === 'idle') return null; let feedbackProps = {}; switch (status) { case 'saving': feedbackProps = { icon: <RefreshCw size={16} />, label: "Salvando...", color: "secondary" }; break; case 'saved': feedbackProps = { icon: <CloudCheck size={16} />, label: "Salvo com sucesso", color: "success" }; break; case 'waiting': feedbackProps = { icon: <FilePenLine size={16} />, label: "Alterações pendentes", color: "warning" }; break; case 'error': feedbackProps = { icon: <AlertTriangle size={16} />, label: "Erro ao salvar", color: "error" }; break; default: return null; } return (<Box sx={{ position: 'fixed', bottom: 24, left: 24, zIndex: 1300 }}><Chip {...feedbackProps} variant="filled" sx={{ boxShadow: 3, animation: `${fadeInUp} 0.3s ease-out` }} /></Box>); };

// --- DIALOG TO QUICKLY CREATE AREA (Unchanged) ---
const CreateAreaDialog = ({ open, onClose, onCreate }) => {
    const [areaName, setAreaName] = useState('');
    const [areaColor, setAreaColor] = useState('#cccccc'); // State for color, default to gray
    const [isCreating, setIsCreating] = useState(false);
    const { token } = useAuth();

    useEffect(() => {
        if (!open) {
            setAreaName('');
            setAreaColor('#cccccc'); // Reset color on close
            setIsCreating(false);
        }
    }, [open]);

    const handleCreateClick = async () => {
        const trimmedName = areaName.trim();
        if (!trimmedName || !areaColor) {
             console.error("Nome e cor são necessários.");
             return;
        }
        setIsCreating(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/areas`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: trimmedName, color: areaColor })
            });
            if (!response.ok) {
                let errorData = { message: `Falha ao criar área. Status: ${response.status}` };
                try { errorData = await response.json(); } catch(e) { /* Ignore */ }
                throw new Error(errorData.message || 'Falha ao criar área.');
            }
            const newArea = await response.json();
            onCreate(newArea);
            onClose();
        } catch (error) {
            console.error("Erro ao criar área:", error);
            alert(`Erro ao criar área: ${error.message}`); // Simple alert for now
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ zIndex: 2500 }}>
            <DialogTitle>Criar Nova Área</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus margin="dense" id="new-area-name" label="Nome da Área" type="text" fullWidth variant="outlined"
                    value={areaName} onChange={(e) => setAreaName(e.target.value)} disabled={isCreating} sx={{ mb: 2 }}
                />
                <Typography variant="caption" display="block" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Cor da Área
                </Typography>
                 <SketchPicker
                    color={areaColor} onChangeComplete={(color) => setAreaColor(color.hex)} width="100%" disableAlpha={true}
                    presetColors={['#f87171', '#fb923c', '#fbbf24', '#a3e635', '#4ade80', '#34d399', '#2dd4bf', '#22d3ee', '#38bdf8', '#60a5fa', '#818cf8', '#a78bfa', '#c084fc', '#e879f9', '#f472b6']}
                    styles={{ default: { picker: { boxShadow: 'none', padding: '0 !important', border: `1px solid ${theme.palette.grey[300]}`, borderRadius: theme.shape.borderRadius } } }}
                 />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isCreating}>Cancelar</Button>
                <Button onClick={handleCreateClick} variant="contained" disabled={!areaName.trim() || !areaColor || isCreating}>
                    {isCreating ? <CircularProgress size={24} /> : 'Criar Área'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- DIALOG TO QUICKLY CREATE CATEGORY (MODIFIED) ---
// Agora é um componente real que salva no backend
const CreateCategoryDialog = ({ open, onClose, onCreate }) => {
    const [categoryName, setCategoryName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const { token } = useAuth(); // <-- Usa o token para autenticação

    useEffect(() => {
        if (!open) {
            setCategoryName('');
            setIsCreating(false);
        }
    }, [open]);

    const handleCreateClick = async () => { // <-- async
        const trimmedName = categoryName.trim();
        if (!trimmedName) {
            console.error("Nome da categoria é necessário.");
            return;
        }
        setIsCreating(true);
        
        // Faz a chamada de API para criar a categoria
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: trimmedName })
            });
            if (!response.ok) {
                let errorData = { message: `Falha ao criar categoria. Status: ${response.status}` };
                try { errorData = await response.json(); } catch(e) { /* Ignore */ }
                throw new Error(errorData.message || 'Falha ao criar categoria.');
            }
            const newCategory = await response.json(); // <-- Recebe o objeto { id, name }
            onCreate(newCategory); // <-- Passa o objeto de volta
            onClose();
        } catch (error) {
            console.error("Erro ao criar categoria:", error);
            alert(`Erro ao criar categoria: ${error.message}`);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ zIndex: 2500 }}>
            <DialogTitle>Criar Nova Categoria</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus margin="dense" id="new-category-name" label="Nome da Categoria" type="text" fullWidth variant="outlined"
                    value={categoryName} onChange={(e) => setCategoryName(e.target.value)} disabled={isCreating} sx={{ mb: 2 }}
                />
                <FormHelperText>
                    Esta categoria será usada para agrupar competências. (Ex: Pacote Office, Ferramentas de Design)
                </FormHelperText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isCreating}>Cancelar</Button>
                <Button onClick={handleCreateClick} variant="contained" disabled={!categoryName.trim() || isCreating}>
                    {isCreating ? <CircularProgress size={24} /> : 'Criar Categoria'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// --- DIALOG TO ADD/EDIT COMPETENCY (MODIFIED) ---
const DialogoCompetencia = ({
    aberto, onClose, onSave, competencia, tipoAtual, nomeTipo,
    areasDisponiveis = [], onNovaAreaCriada,
    categoriasDisponiveis = [], // <-- Esta prop agora é [{id, name}, ...]
    onNovaCategoriaCriada,
    onDeleteCategory
}) => {
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [areaIdSelecionada, setAreaIdSelecionada] = useState('');
    const [categoriaIdSelecionada, setCategoriaIdSelecionada] = useState(''); // <-- MUDADO de 'categoria' para 'categoriaIdSelecionada'
    const [isCreateAreaDialogOpen, setIsCreateAreaDialogOpen] = useState(false);
    const [isCreateCategoryDialogOpen, setIsCreateCategoryDialogOpen] = useState(false);

    const isTecnicaOuOutra = tipoAtual === 'tecnicas' || tipoAtual === 'outras';

    useEffect(() => {
        if (aberto) {
            setNome(competencia?.nome || '');
            setDescricao(competencia?.descricao || '');
            setAreaIdSelecionada(isTecnicaOuOutra ? (competencia?.areaId?.toString() || '') : '');
            setCategoriaIdSelecionada(isTecnicaOuOutra ? (competencia?.categoriaId?.toString() || '') : ''); // <-- USA categoriaId
        } else {
             setNome(''); setDescricao(''); setAreaIdSelecionada(''); setCategoriaIdSelecionada('');
        }
    }, [competencia, aberto, isTecnicaOuOutra]);

    const handleSaveClick = () => {
        if (nome.trim() && descricao.trim()) {
            const competenciaData = { 
                nome: nome.trim(), 
                descricao: descricao.trim() 
            };
            
            if (isTecnicaOuOutra) {
                competenciaData.areaId = areaIdSelecionada || null;
                competenciaData.categoriaId = categoriaIdSelecionada || null; // <-- ENVIA categoriaId
            }
            
            console.log('Salvando Competência:', competenciaData); // Debug Log
            onSave(competenciaData);
        }
    };

    const handleAreaChange = (event) => {
        const value = event.target.value;
        if (value === '___CREATE_NEW___') {
            setIsCreateAreaDialogOpen(true);
        } else {
            setAreaIdSelecionada(value);
        }
    };

    const handleNovaAreaCallback = (novaArea) => {
        if (novaArea && novaArea.id) {
            onNovaAreaCriada(novaArea); 
            setAreaIdSelecionada(novaArea.id.toString());
        }
         setIsCreateAreaDialogOpen(false);
    };

    // --- Handlers de Categoria MODIFICADOS ---
    const handleCategoryChange = (event) => {
        const value = event.target.value;
        if (value === '___CREATE_NEW___') {
            setIsCreateCategoryDialogOpen(true);
        } else {
            setCategoriaIdSelecionada(value); // <-- Salva o ID
        }
    };

    const handleNovaCategoriaCallback = (novaCategoria) => { // <-- Recebe o objeto {id, name}
        if (novaCategoria && novaCategoria.id) {
            onNovaCategoriaCriada(novaCategoria); // <-- Passa o objeto para a página principal
            setCategoriaIdSelecionada(novaCategoria.id.toString()); // <-- Seleciona o ID
        }
        setIsCreateCategoryDialogOpen(false);
    };

    const tituloDialogo = `${competencia ? 'Editar' : 'Adicionar'} Competência ${nomeTipo}`;

    return (
        <>
            <Dialog open={aberto} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { border: `1px solid ${theme.palette.grey[200]}` } }} sx={{ zIndex: 2000 }}>
                <DialogTitle sx={{ fontWeight: '600', pb: 1.5 }}>{tituloDialogo} <IconButton onClick={onClose} sx={{ position: 'absolute', right: 16, top: 16, color: 'grey.500' }}><XLucide size={20} /></IconButton></DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <TextField autoFocus margin="dense" id="nome" label="Nome da Competência" type="text" fullWidth variant="outlined" value={nome} onChange={(e) => setNome(e.target.value)} />
                    <TextField margin="dense" id="descricao" label="Descrição da Competência" type="text" fullWidth multiline rows={5} variant="outlined" value={descricao} onChange={(e) => setDescricao(e.target.value)} sx={{ mt: 2 }} />
                    
                    {isTecnicaOuOutra && (
                        <>
                            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                                <InputLabel id="area-select-label">Área (Opcional)</InputLabel>
                                <Select
    labelId="category-select-label" 
    id="category-select" 
    value={categoriaIdSelecionada}
    label="Categoria (Opcional)"
    onChange={handleCategoryChange} 
    MenuProps={{ disablePortal: true }}
    renderValue={(selected) => {
        if (!selected) return <em>Nenhuma Categoria Específica</em>;
        const categoria = categoriasDisponiveis.find(cat => cat.id.toString() === selected);
        return categoria ? categoria.name : '';
    }}
>
                                    <MenuItem value=""><em>Nenhuma Área Específica</em></MenuItem>
                                    {areasDisponiveis.map((area) => (<MenuItem key={area.id} value={area.id.toString()}>{area.name}</MenuItem>))}
                                    <Divider />
                                    <MenuItem value={'___CREATE_NEW___'}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.success.main }}><Plus size={16} /> Criar Nova Área...</Box></MenuItem>
                                </Select>
                                <FormHelperText>Associe esta competência a uma área (Ex: Administrativo, TI).</FormHelperText>
                            </FormControl>

                            {/* CAMPO DE CATEGORIA MODIFICADO para usar objetos {id, name} */}
                            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                                <InputLabel id="category-select-label">Categoria (Opcional)</InputLabel>
                                <Select
    labelId="category-select-label" 
    id="category-select" 
    value={categoriaIdSelecionada}
    label="Categoria (Opcional)"
    onChange={handleCategoryChange} 
    MenuProps={{ disablePortal: true }}
    renderValue={(selected) => {
        if (!selected || selected === '') {
            return <em style={{ fontStyle: 'normal', color: 'inherit' }}>Nenhuma Categoria Específica</em>;
        }
        const categoria = categoriasDisponiveis.find(cat => cat.id.toString() === selected);
        return categoria ? categoria.name : '';
    }}
>
    <MenuItem value="">
        <em>Nenhuma Categoria Específica</em>
    </MenuItem>
    {categoriasDisponiveis.map((cat) => (
        <MenuItem 
            key={cat.id} 
            value={cat.id.toString()} 
            sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '48px',
                px: 2,
                py: 1
            }}
        >
            <Typography 
                sx={{ 
                    flex: '1 1 auto',
                    pr: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {cat.name}
            </Typography>
            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCategory(cat);
                    }}
                    size="small"
                    sx={{ 
                        color: theme.palette.error.main,
                        padding: '4px',
                        '&:hover': { 
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            transform: 'scale(1.15)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    <Trash2 size={16} />
                </IconButton>
            </Box>
        </MenuItem>
    ))}
    <Divider />
    <MenuItem value={'___CREATE_NEW___'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.success.main }}>
            <Plus size={18} />
            <Typography>Criar Nova Categoria</Typography>
        </Box>
    </MenuItem>
</Select>
                                <FormHelperText>Agrupe competências (Ex: Pacote Office) ou crie uma nova.</FormHelperText>
                            </FormControl>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} color="secondary">Cancelar</Button>
                    <Button onClick={handleSaveClick} variant="contained" disabled={!nome.trim() || !descricao.trim()}>{competencia ? 'Salvar Alterações' : 'Adicionar'}</Button>
                </DialogActions>
            </Dialog>
            
            {/* Dialogs de criação */}
            <CreateCategoryDialog open={isCreateCategoryDialogOpen} onClose={() => setIsCreateCategoryDialogOpen(false)} onCreate={handleNovaCategoriaCallback} />
            <CreateAreaDialog open={isCreateAreaDialogOpen} onClose={() => setIsCreateAreaDialogOpen(false)} onCreate={handleNovaAreaCallback} />
        </>
    );
};

// --- DIALOG CONFIRM CREATE AREAS FROM CSV (Unchanged) ---
const ConfirmCreateAreasDialog = ({ open, onClose, areasToCreate: initialAreas = [], onConfirm }) => {
    const [areasWithColors, setAreasWithColors] = useState([]);

    useEffect(() => {
        if (open) {
            setAreasWithColors(initialAreas.map(name => ({ name: name, color: '#cccccc' }))); // Initialize with default color
        }
    }, [open, initialAreas]);

    const handleColorChange = (index, newColor) => {
        setAreasWithColors(currentAreas => currentAreas.map((area, i) => i === index ? { ...area, color: newColor } : area));
    };

    const handleConfirmClick = () => {
         const hexColorRegex = /^#[0-9A-F]{6}$/i;
         const invalidColor = areasWithColors.find(a => !hexColorRegex.test(a.color));
         if (invalidColor) {
             alert(`Cor inválida "${invalidColor.color}" para a área "${invalidColor.name}". Use o formato #RRGGBB.`);
             return;
         }
         onConfirm(areasWithColors); // Pass array of { name, color }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth sx={{ zIndex: confirmationModalOverlayStyle.zIndex }}>
            <DialogTitle>Criar Novas Áreas do CSV</DialogTitle>
            <DialogContent dividers>
                <Typography gutterBottom>As seguintes áreas mencionadas no arquivo CSV não existem.</Typography>
                <Alert severity="info" sx={{ my: 2 }}>Para criar estas áreas, por favor, defina uma cor para cada uma. A cor ajuda a identificar visualmente as áreas no sistema. Use o formato hexadecimal (#RRGGBB).</Alert>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', my: 2, p: 1 }}>
                    <List dense disablePadding>
                        {areasWithColors.map((area, index) => (
                            <ListItem key={index} disableGutters sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                                <ListItemText primary={area.name} sx={{ flexGrow: 1 }} />
                                {/* Input de Cor */}
                                <Input
                                    type="color" value={area.color} onChange={(e) => handleColorChange(index, e.target.value)}
                                    sx={{ width: '40px', height: '30px', padding: '2px', border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '4px', cursor: 'pointer', '& input[type="color"]::-webkit-color-swatch': { borderRadius: '3px', border: 'none', }, '& input[type="color"]::-moz-color-swatch': { borderRadius: '3px', border: 'none', } }}
                                />
                                 {/* Input de Texto Hex */}
                                 <TextField
                                    variant="outlined" size="small" value={area.color} onChange={(e) => handleColorChange(index, e.target.value)}
                                    inputProps={{ maxLength: 7, pattern: "^#[0-9A-Fa-f]{6}$" }} sx={{ width: '100px' }}
                                 />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
                <Typography>Deseja criar essas áreas com as cores definidas e prosseguir com a importação?</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => onClose(false)} color="secondary">Não, Cancelar Importação</Button>
                <Button onClick={handleConfirmClick} variant="contained" color="success">Sim, Criar Áreas e Importar</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MODAL DE EXCLUSÃO DE MASSA (Unchanged) ---
const ModalExclusaoMassa = ({ open, onClose, onConfirm, tipoCompetencia, isDeleting }) => {
    const [confirmacao, setConfirmacao] = useState('');
    const [erro, setErro] = useState('');
    const fraseConfirmacao = `Excluir ${tipoCompetencia}`;
    
    useEffect(() => {
        if (open) {
            setConfirmacao('');
            setErro('');
        }
    }, [open]);

    const handleConfirmacao = () => {
        if (confirmacao.trim() === fraseConfirmacao) {
            setErro('');
            onConfirm();
        } else {
            setErro('A frase digitada não corresponde à confirmação. Por favor, tente novamente.');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: 2500 }}>
            <DialogTitle color="error">Excluir Todas as Competências {tipoCompetencia}?</DialogTitle>
            <DialogContent dividers>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Esta ação é <span style={{ fontWeight: 'bold' }}>IRREVERSÍVEL</span> e removerá todas as competências do tipo **{tipoCompetencia}**.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Para confirmar, digite exatamente a frase abaixo no campo:
                </Typography>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        p: 1.5, 
                        mb: 2, 
                        border: '1px dashed', 
                        borderColor: 'error.main', 
                        bgcolor: alpha(theme.palette.error.main, 0.05),
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: theme.palette.error.dark
                    }}
                >
                    {fraseConfirmacao}
                </Typography>

                <TextField
                    fullWidth
                    label="Digite a frase de confirmação"
                    value={confirmacao}
                    onChange={(e) => setConfirmacao(e.target.value)}
                    error={!!erro}
                    helperText={erro}
                    disabled={isDeleting}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isDeleting}>Cancelar</Button>
                <Button 
                    onClick={handleConfirmacao} 
                    color="error" 
                    variant="contained" 
                    disabled={confirmacao.trim() !== fraseConfirmacao || isDeleting}
                    startIcon={isDeleting && <CircularProgress size={20} color="inherit" />}
                >
                    {isDeleting ? 'Excluindo...' : `Excluir ${tipoCompetencia}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MODAL DE IMPORTAÇÃO (Unchanged) ---
const ModalImportacao = ({ open, onClose, onFileSelect }) => {
    const [file, setFile] = useState(null); const [fileName, setFileName] = useState(''); const [error, setError] = useState(''); const [isProcessing, setIsProcessing] = useState(false);
    const processFile = (selectedFile) => { if (selectedFile) { if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) { setError('Formato inválido. Por favor, envie um arquivo .csv'); setFile(null); setFileName(''); } else { setFile(selectedFile); setFileName(selectedFile.name); setError(''); } } };
    const onDrop = useCallback((acceptedFiles) => { processFile(acceptedFiles[0]); }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'text/csv': ['.csv'] }, multiple: false, });
    const handleImportClick = async () => { if (!file) { setError('Por favor, selecione um arquivo.'); return; } setIsProcessing(true); setError(''); onFileSelect(file); setIsProcessing(false); };
    useEffect(() => { if (!open) { setFile(null); setFileName(''); setError(''); setIsProcessing(false); } }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ zIndex: 2000 }}>
            <DialogTitle fontWeight="bold">Importar Competências via CSV</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ p: 1 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>Formato e Colunas</Typography>
                        
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: theme.palette.background.paper }}>
                            
                            <Typography variant="body1" component="div" mb={2}>
                                O arquivo deve ser <Typography component="strong" sx={{ fontWeight: 600 }}>.csv</Typography> e conter as seguintes colunas (no cabeçalho):
                            </Typography>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {[{ field: 'tipo', desc: 'Categoria da competência.', example: 'tecnicas, corporativas' },
                                  { field: 'nome', desc: 'Título Curto (Ex: Liderança).', example: 'Liderança' },
                                  { field: 'descricao', desc: 'Explicação Detalhada.', example: 'Comportamento...' },
                                  { field: 'categoria', desc: 'Agrupador (p/ Técnicas/Outras).', example: 'Pacote Office' }
                                ].map((item, index) => (
                                    <Grid item xs={12} sm={3} key={index}>
                                        <Typography component="div" sx={{ border: `1px solid ${theme.palette.grey[300]}`, p: 1, borderRadius: 1, height: '100%' }}>
                                            <Typography component="strong" fontWeight="bold">{item.field}</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                                {item.desc}
                                                <Chip label={`Ex: ${item.example}`} size="small" sx={{ mt: 0.5, height: 18, bgcolor: theme.palette.grey[100], color: theme.palette.text.primary, fontWeight: 500 }} />
                                            </Typography>
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            
                            <Box sx={{ p: 1.5, border: `2px solid ${theme.palette.grey[300]}`, borderRadius: 1, mt: 2 }}>
                                <Typography fontWeight="bold" mb={1} sx={{ color: theme.palette.text.primary }}>Diferença de Área e Categoria (Colunas `area` e `categoria`):</Typography>

                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, borderBottom: `1px dashed ${theme.palette.grey[300]}`, pb: 0.5 }}>
                                            Tipos Técnicas / Outras:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mt={1}>
                                            As colunas `area` e `categoria` são **opcionais** e recomendadas.
                                            <Chip label="area: Financeiro" size="small" sx={{ mt: 0.5, height: 18, bgcolor: theme.palette.grey[100], color: theme.palette.text.primary }} />
                                            <Chip label="categoria: Pacote Office" size="small" sx={{ mt: 0.5, ml: 0.5, height: 18, bgcolor: theme.palette.grey[100], color: theme.palette.text.primary }} />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, borderBottom: `1px dashed ${theme.palette.grey[300]}`, pb: 0.5 }}>
                                            Tipos Comportamentais:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mt={1}>
                                            As colunas `area` e `categoria` devem ser **deixadas em branco** (ou omitidas).
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Typography variant="caption" display="block" color="text.secondary" mt={2} sx={{ borderTop: `1px solid ${theme.palette.grey[200]}`, pt: 1, fontStyle: 'italic' }}>
                                **Exemplo de Linha CSV:** `tecnicas,Excel (Avançado),Domina a criação de tabelas,Financeiro,Pacote Office`
                            </Typography>
                            
                        </Paper>
                    </Box>

                    <Box {...getRootProps()} sx={{ border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'grey.400', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer', backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent', transition: 'border-color 0.3s, background-color 0.3s' }}>
                        <input {...getInputProps()} /> <UploadFile sx={{ fontSize: 40, color: 'grey.600', mb: 1 }} />
                        {isDragActive ? (<Typography fontWeight="bold">Solte o arquivo aqui...</Typography>) : (<Typography>Arraste e solte o arquivo <Typography component="span" sx={{ fontWeight: 600 }}>.csv</Typography> aqui, ou clique para selecionar.</Typography>)}
                    </Box>
                    {fileName && <Typography variant="body2" color="text.secondary" align="center">Arquivo selecionado: <Typography component="span" sx={{ fontWeight: 600 }}>{fileName}</Typography></Typography>}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleImportClick} variant="contained" disabled={!file || isProcessing}>
                    {isProcessing ? <CircularProgress size={24} /> : 'Processar Arquivo'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MAIN PAGE COMPONENT (MODIFIED) ---
const PaginaCompetenciasProfissional = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { token } = useAuth();

    // State for competencies
    const [categoriaPrincipal, setCategoriaPrincipal] = useState('comportamentais');
    const [subCategoria, setSubCategoria] = useState('corporativas');
    const [corporativas, setCorporativas] = useState([]);
    const [gerenciais, setGerenciais] = useState([]);
    const [tecnicas, setTecnicas] = useState([]);
    const [outras, setOutras] = useState([]);

    // State for Areas and Categories (MODIFIED)
    const [areas, setAreas] = useState([]);
    const [isLoadingAreas, setIsLoadingAreas] = useState(true);
    const [categories, setCategories] = useState([]); // <-- NOVO: Lista de objetos {id, name}
    const [isLoadingCategories, setIsLoadingCategories] = useState(true); // <-- NOVO
    // REMOVIDO: extraCategorias não é mais necessário

    // State for UI and Modals
    const [dialogoAberto, setDialogoAberto] = useState(false);
    const [competenciaEmEdicao, setCompetenciaEmEdicao] = useState(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExplanatoryVideoModalOpen, setIsExplanatoryVideoModalOpen] = useState(false);
    const [confirmCreateAreasData, setConfirmCreateAreasData] = useState({ open: false, areasInfo: [], parsedData: [] });
    
    // State for Mass Delete
    const [isMassDeleteModalOpen, setIsMassDeleteModalOpen] = useState(false);
    const [tipoParaExcluir, setTipoParaExcluir] = useState('');
    const [isDeletingMass, setIsDeletingMass] = useState(false);
    const [deleteCategoryConfirm, setDeleteCategoryConfirm] = useState({ isOpen: false, category: null });
    // State for Control and Feedback
    const [carregando, setCarregando] = useState(true);
    const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: '', tipo: 'success' });
    const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
    const isDirtyRef = useRef(false);
    const saveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);
    const MAX_COMPETENCIAS = 15;

    const handleApiError = useCallback((error, context = "Operação") => {
        console.error(`${context} falhou:`, error);
        const message = error instanceof Error ? error.message : String(error);
        setNotificacao({ aberta: true, mensagem: `${context}: ${message || 'Erro desconhecido.'}`, tipo: 'error' });
    }, []);

    // Initial fetch for Competencies, Areas, AND Categories (MODIFIED)
    useEffect(() => {
        if (token) {
            setCarregando(true); setIsLoadingAreas(true); setIsLoadingCategories(true); // <-- Seta ambos
            Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/company/competencies`, { headers: { 'Authorization': `Bearer ${token}` } }).then(res => res.ok ? res.json() : res.text().then(text => Promise.reject(`Falha ao carregar competências: ${text}`))),
                fetch(`${process.env.REACT_APP_API_URL}/areas`, { headers: { 'Authorization': `Bearer ${token}` } }).then(res => res.ok ? res.json() : res.text().then(text => Promise.reject(`Falha ao carregar áreas: ${text}`))),
                fetch(`${process.env.REACT_APP_API_URL}/categories`, { headers: { 'Authorization': `Bearer ${token}` } }).then(res => res.ok ? res.json() : res.text().then(text => Promise.reject(`Falha ao carregar categorias: ${text}`))) // <-- NOVO FETCH
            ]).then(([competenciesData, areasData, categoriesData]) => { // <-- Recebe 3 resultados
                console.log('Dados recebidos do GET (Compet.):', competenciesData);
                console.log('Dados recebidos do GET (Areas):', areasData);
                console.log('Dados recebidos do GET (Categories):', categoriesData);
                
                setCorporativas(Array.isArray(competenciesData.corporativas) ? competenciesData.corporativas : []);
                setGerenciais(Array.isArray(competenciesData.gerenciais) ? competenciesData.gerenciais : []);
                setTecnicas(Array.isArray(competenciesData.tecnicas) ? competenciesData.tecnicas : []);
                setOutras(Array.isArray(competenciesData.outras) ? competenciesData.outras : []);
                
                setAreas(Array.isArray(areasData) ? areasData : []);
                setCategories(Array.isArray(categoriesData) ? categoriesData : []); // <-- Salva as categorias
                
                setTimeout(() => { initialLoadRef.current = false; }, 100);
            }).catch(error => handleApiError(error, "Carregamento inicial"))
              .finally(() => { 
                  setCarregando(false); 
                  setIsLoadingAreas(false); 
                  setIsLoadingCategories(false); // <-- Finaliza o loading
              });
        } else {
            setCarregando(false); setIsLoadingAreas(false); setIsLoadingCategories(false);
            setNotificacao({aberta: true, mensagem: 'Autenticação necessária.', tipo: 'warning'});
        }
    }, [token, handleApiError]);

    // --- Save Dictionary (Auto-Save) (MODIFIED) ---
    // Agora envia 'categoriaId'
    const handleSalvarDicionario = useCallback(async () => {
        if (!isDirtyRef.current) return;
        setAutoSaveStatus('saving');
        try {
            const payload = {
                corporativas,
                gerenciais,
                tecnicas: tecnicas.map(({ areaId, categoriaId, ...comp }) => ({ // <-- MUDADO para categoriaId
                    ...comp, 
                    areaId: areaId || null,
                    categoriaId: categoriaId || null // <-- MUDADO para categoriaId
                })),
                outras: outras.map(({ areaId, categoriaId, ...comp }) => ({ // <-- MUDADO para categoriaId
                    ...comp, 
                    areaId: areaId || null,
                    categoriaId: categoriaId || null // <-- MUDADO para categoriaId
                })),
            };
            console.log('DEBUG: Payload enviado para PUT /company/competencies:', JSON.stringify(payload, null, 2));
            const res = await fetch(`${process.env.REACT_APP_API_URL}/company/competencies`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload)
            });
            if (!res.ok) { const errorText = await res.text(); throw new Error(errorText || 'Falha ao salvar o dicionário.'); }
            isDirtyRef.current = false; setAutoSaveStatus('saved'); setTimeout(() => setAutoSaveStatus('idle'), 2000);
        } catch (error) { handleApiError(error, "Salvar dicionário"); setAutoSaveStatus('error'); }
    }, [corporativas, gerenciais, tecnicas, outras, token, handleApiError]);

    // Trigger Auto-Save (Unchanged from last fix)
    useEffect(() => {
        if (initialLoadRef.current || !isDirtyRef.current) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        setAutoSaveStatus('waiting'); saveTimeoutRef.current = setTimeout(() => { handleSalvarDicionario(); }, 1500);
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, [corporativas, gerenciais, tecnicas, outras]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Process CSV File (MODIFIED) ---
    // Agora mapeia nomes de categoria para IDs e auto-cria categorias faltantes
    const processCsvFile = (file) => {
        if (!file) return;
        setIsImportModalOpen(false); setCarregando(true);
        Papa.parse(file, {
            header: true, skipEmptyLines: true,
            complete: async (results) => { // <-- Tornou-se async
                const parsedCompetencias = { corporativas: [], gerenciais: [], tecnicas: [], outras: [] };
                const validationErrors = []; 
                const unknownAreaNames = new Set();
                const unknownCategoryNames = new Set(); // <-- NOVO

                const areaMap = new Map(areas.map(a => [a.name.toLowerCase(), a.id]));
                const categoryMap = new Map(categories.map(c => [c.name.toLowerCase(), c.id])); // <-- NOVO
                const validTypes = ['corporativas', 'gerenciais', 'tecnicas', 'outras'];

                results.data.forEach((row, index) => {
                    const lineNumber = index + 2;
                    const { tipo, nome, descricao, area: areaNomeCsv, categoria: categoriaCsv } = row;
                    
                    const trimmedType = tipo?.trim().toLowerCase(); 
                    const trimmedName = nome?.trim();
                    const trimmedDesc = descricao?.trim(); 
                    const trimmedArea = areaNomeCsv?.trim();
                    const trimmedCategoria = categoriaCsv?.trim();

                    if (!trimmedType || !trimmedName || !trimmedDesc) { validationErrors.push(`Linha ${lineNumber}: Faltam dados obrigatórios.`); return; }
                    if (!validTypes.includes(trimmedType)) { validationErrors.push(`Linha ${lineNumber}: Tipo "${tipo}" inválido.`); return; }
                    
                    const competenciaData = { 
                        id: `temp-${Date.now()}-${index}`, 
                        nome: trimmedName, 
                        descricao: trimmedDesc, 
                        areaId: null,
                        categoriaId: null // <-- MUDADO para categoriaId
                    };

                    if ((trimmedType === 'tecnicas' || trimmedType === 'outras')) {
                        // Lógica da Área (sem mudança)
                        if (trimmedArea) {
                            const areaNameLower = trimmedArea.toLowerCase();
                            if (areaMap.has(areaNameLower)) { 
                                competenciaData.areaId = areaMap.get(areaNameLower); 
                            } else { 
                                unknownAreaNames.add(trimmedArea); 
                                competenciaData.areaNameToResolve = trimmedArea; 
                            }
                        }
                        
                        // Lógica da Categoria (MODIFICADA para IDs)
                        if (trimmedCategoria) {
                            const catNameLower = trimmedCategoria.toLowerCase();
                            if (categoryMap.has(catNameLower)) {
                                competenciaData.categoriaId = categoryMap.get(catNameLower);
                            } else {
                                unknownCategoryNames.add(trimmedCategoria);
                                competenciaData.categoriaNameToResolve = trimmedCategoria;
                            }
                        }
                    }
                    parsedCompetencias[trimmedType].push(competenciaData);
                });

                if (validationErrors.length > 0) { setNotificacao({ aberta: true, mensagem: `Erros no CSV:\n${validationErrors.join('\n')}`, tipo: 'error' }); setCarregando(false); return; }

                // --- NOVO: Auto-criação de Categorias ---
                const newCategoryNames = Array.from(unknownCategoryNames);
                if (newCategoryNames.length > 0) {
                    setNotificacao({ aberta: true, mensagem: `Criando ${newCategoryNames.length} nova(s) categoria(s) do CSV...`, tipo: 'info' });
                    try {
                        const creationPromises = newCategoryNames.map(name =>
                            fetch(`${process.env.REACT_APP_API_URL}/categories`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ name: name })
                            }).then(res => res.ok ? res.json() : Promise.reject(`Falha ao auto-criar categoria "${name}"`))
                        );
                        
                        const newCats = await Promise.all(creationPromises);
                        
                        // Atualiza o map e o estado principal
                        const validNewCats = [];
                        newCats.forEach(cat => {
                            if (cat && cat.id) {
                                categoryMap.set(cat.name.toLowerCase(), cat.id); // Atualiza o map para o 'addCompetencies'
                                validNewCats.push(cat);
                            }
                        });
                        setCategories(prev => [...prev, ...validNewCats]);
                        
                    } catch (error) {
                        handleApiError(error, "Auto-criação de categoria CSV");
                        // Continua mesmo se falhar, as competências ficarão sem categoria
                    }
                }
                // --- Fim da auto-criação ---

                if (unknownAreaNames.size > 0) { 
                    setConfirmCreateAreasData({ open: true, areasInfo: Array.from(unknownAreaNames), parsedData: parsedCompetencias }); 
                    setCarregando(false); 
                } else { 
                    addCompetenciesToState(parsedCompetencias, new Map(), categoryMap); // Passa o categoryMap atualizado
                    setNotificacao({ aberta: true, mensagem: 'Competências importadas com sucesso!', tipo: 'success' }); 
                    setCarregando(false); 
                }
            },
            error: (error) => { console.error("Erro ao parsear CSV:", error); setNotificacao({ aberta: true, mensagem: `Falha ao ler CSV: ${error.message}`, tipo: 'error' }); setCarregando(false); },
        });
    };

    // Helper to add competencies to state (MODIFIED)
    // Agora resolve 'categoriaId'
    const addCompetenciesToState = (competenciasToAdd, newAreaIdMap = new Map(), categoryIdMap) => {
        const resolveAreaId = (comp) => {
             if (comp.areaId) return comp.areaId;
             if (comp.areaNameToResolve && newAreaIdMap.has(comp.areaNameToResolve)) { return newAreaIdMap.get(comp.areaNameToResolve); }
             return null;
        }
        // NOVO: Função para resolver ID da Categoria
        const resolveCategoriaId = (comp) => {
            if (comp.categoriaId) return comp.categoriaId;
            if (comp.categoriaNameToResolve && categoryIdMap.has(comp.categoriaNameToResolve.toLowerCase())) { 
                return categoryIdMap.get(comp.categoriaNameToResolve.toLowerCase()); 
            }
            return null;
        }
        
        setCorporativas(prev => [...prev, ...competenciasToAdd.corporativas]);
        setGerenciais(prev => [...prev, ...competenciasToAdd.gerenciais]);
        setTecnicas(prev => [...prev, ...competenciasToAdd.tecnicas.map(c => ({...c, areaId: resolveAreaId(c), categoriaId: resolveCategoriaId(c) }))]);
        setOutras(prev => [...prev, ...competenciasToAdd.outras.map(c => ({...c, areaId: resolveAreaId(c), categoriaId: resolveCategoriaId(c) }))]);
        isDirtyRef.current = true;
    };

    // Handle confirmation for creating areas from CSV (MODIFIED)
    // Agora passa o categoryMap para 'addCompetenciesToState'
    const handleConfirmCreateAreas = async (areasToCreateWithColor) => {
        const { parsedData } = confirmCreateAreasData;
        setConfirmCreateAreasData({ open: false, areasInfo: [], parsedData: [] }); 

        // Pega o estado ATUAL do categoryMap
        const categoryMap = new Map(categories.map(c => [c.name.toLowerCase(), c.id]));

        if (!Array.isArray(areasToCreateWithColor) || areasToCreateWithColor.length === 0) {
             const competenciesWithoutUnknownAreas = {
                 corporativas: parsedData.corporativas,
                 gerenciais: parsedData.gerenciais,
                 tecnicas: parsedData.tecnicas.filter(c => !c.areaNameToResolve),
                 outras: parsedData.outras.filter(c => !c.areaNameToResolve)
             };
             const skippedCount = (parsedData.tecnicas.filter(c => c.areaNameToResolve).length || 0) + (parsedData.outras.filter(c => c.areaNameToResolve).length || 0);
             addCompetenciesToState(competenciesWithoutUnknownAreas, new Map(), categoryMap); // <-- Passa o categoryMap
             setNotificacao({ aberta: true, mensagem: `Importação parcial concluída. ${skippedCount} competência(s) com áreas desconhecidas foram ignoradas.`, tipo: 'warning' });
            return;
        }

        setCarregando(true);
        try {
            // ... (Lógica de criação de Áreas - Inalterada) ...
            const creationPromises = areasToCreateWithColor.map(areaInfo =>
                 fetch(`${process.env.REACT_APP_API_URL}/areas`, {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                     body: JSON.stringify({ name: areaInfo.name, color: areaInfo.color })
                 }).then(res => { 
                    if (!res.ok) { return res.text().then(text => { try { const jErr = JSON.parse(text); throw new Error(jErr.message || `Falha ao criar área "${areaInfo.name}"`); } catch { throw new Error(text || `Falha ao criar área "${areaInfo.name}"`); } }); }
                    if (res.status === 204 || res.headers.get("content-length") === "0") { return null; }
                    return res.json();
                 })
            );
             const results = await Promise.allSettled(creationPromises);
             const newAreaIdMap = new Map();
             const successfullyCreatedAreas = [];
             let creationFailed = false;
             results.forEach((result, index) => {
                 const areaName = areasToCreateWithColor[index].name;
                 if (result.status === 'fulfilled' && result.value && result.value.id) {
                     successfullyCreatedAreas.push(result.value);
                     newAreaIdMap.set(areaName, result.value.id);
                 } else {
                     creationFailed = true;
                     const reason = result.reason instanceof Error ? result.reason.message : String(result.reason);
                     console.error(`Falha ao criar área "${areaName}":`, reason);
                     setNotificacao({ aberta: true, mensagem: `Falha ao criar área "${areaName}": ${reason}`, tipo: 'error' });
                 }
             });
            if (successfullyCreatedAreas.length > 0) {
                 setAreas(prev => [...prev, ...successfullyCreatedAreas]);
            }
            // ... (Fim da lógica de criação de Áreas) ...

            addCompetenciesToState(parsedData, newAreaIdMap, categoryMap); // <-- Passa o categoryMap
            setNotificacao({
                aberta: true,
                mensagem: creationFailed ? 'Competências importadas, mas algumas áreas falharam na criação.' : 'Áreas criadas e competências importadas com sucesso!',
                tipo: creationFailed ? 'warning' : 'success'
            });

        } catch (error) {
            handleApiError(error, "Criar áreas via CSV");
        } finally {
            setCarregando(false);
        }
    };

    // --- FUNÇÕES DE EXCLUSÃO DE MASSA (MODIFIED) ---
    const tipoMap = {
        corporativas: 'Corporativas',
        gerenciais: 'Gerenciais',
        tecnicas: 'Técnicas',
        outras: 'Outras',
    };

    const handleAbrirExclusaoMassa = useCallback((tipoLabel) => {
        setTipoParaExcluir(tipoLabel);
        setIsMassDeleteModalOpen(true);
    }, []);

    const handleFecharModalExclusao = () => {
        setIsMassDeleteModalOpen(false);
        setTipoParaExcluir('');
    };

  const handleConfirmarExclusaoMassa = async () => {
        setIsDeletingMass(true);
        const tipoSlug = tipoAtual;

        try {
            // Usa o payload com 'categoriaId'
            const finalPayload = {
                 corporativas: corporativas,
                 gerenciais: gerenciais,
                 tecnicas: tecnicas.map(({ areaId, categoriaId, ...comp }) => ({ ...comp, areaId: areaId || null, categoriaId: categoriaId || null })),
                 outras: outras.map(({ areaId, categoriaId, ...comp }) => ({ ...comp, areaId: areaId || null, categoriaId: categoriaId || null }))
            };
            
            // Zera o tipo correto
            if (tipoSlug === 'corporativas') finalPayload.corporativas = [];
            else if (tipoSlug === 'gerenciais') finalPayload.gerenciais = [];
            else if (tipoSlug === 'tecnicas') finalPayload.tecnicas = [];
            else if (tipoSlug === 'outras') finalPayload.outras = [];
            
            const res = await fetch(`${process.env.REACT_APP_API_URL}/company/competencies`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(finalPayload) // <-- Envia o payload mapeado
            });

            if (!res.ok) {
                const errorText = await res.text(); 
                throw new Error(errorText || `Falha ao excluir as competências ${tipoParaExcluir}.`); 
            }
            
            switch (tipoSlug) {
                case 'corporativas': setCorporativas([]); break;
                case 'gerenciais': setGerenciais([]); break;
                case 'tecnicas': setTecnicas([]); break;
                case 'outras': setOutras([]); break;
                default: break;
            }
            isDirtyRef.current = true;
            
            setNotificacao({ aberta: true, mensagem: `Todas as Competências ${tipoParaExcluir} foram removidas com sucesso!`, tipo: 'success' });

        } catch (error) {
            handleApiError(error, `Exclusão em massa de ${tipoParaExcluir}`);
        } finally {
            setIsDeletingMass(false);
            handleFecharModalExclusao();
        }
    };
    // --- FIM DAS FUNÇÕES DE EXCLUSÃO DE MASSA ---

    // Map current data based on category/subcategory (Unchanged)
    const { dadosAtuais, setDadosAtuais, tipoAtual, nomeTipoAtual } = useMemo(() => {
        switch (categoriaPrincipal) {
            case 'comportamentais': return subCategoria === 'corporativas' ? { dadosAtuais: corporativas, setDadosAtuais: setCorporativas, tipoAtual: 'corporativas', nomeTipoAtual: tipoMap.corporativas } : { dadosAtuais: gerenciais, setDadosAtuais: setGerenciais, tipoAtual: 'gerenciais', nomeTipoAtual: tipoMap.gerenciais };
            case 'tecnicas': return { dadosAtuais: tecnicas, setDadosAtuais: setTecnicas, tipoAtual: 'tecnicas', nomeTipoAtual: tipoMap.tecnicas };
            case 'outras': return { dadosAtuais: outras, setDadosAtuais: setOutras, tipoAtual: 'outras', nomeTipoAtual: tipoMap.outras };
            default: return { dadosAtuais: [], setDadosAtuais: () => {}, tipoAtual: '', nomeTipoAtual: '' };
        }
    }, [categoriaPrincipal, subCategoria, corporativas, gerenciais, tecnicas, outras]);

    // Open/Close competency dialog (Unchanged)
    const abrirDialogo = (competencia = null) => { setCompetenciaEmEdicao(competencia); setDialogoAberto(true); };
    const fecharDialogo = () => { setDialogoAberto(false); setCompetenciaEmEdicao(null); };

    // Save individual competency (from dialog) (MODIFIED)
    // 'compData' agora contém 'categoriaId'
    const handleSalvarCompetencia = (compData) => { 
        isDirtyRef.current = true;
        const newId = `temp-${Date.now()}`;
        if (competenciaEmEdicao) {
            setDadosAtuais(dadosAtuais.map(c => c.id === competenciaEmEdicao.id ? { ...c, ...compData } : c));
        } else {
            setDadosAtuais([...dadosAtuais, { id: newId, ...compData }]);
        }
        fecharDialogo();
    };

    // Delete competency (Unchanged)
    const deletarCompetencia = (idParaDeletar) => { isDirtyRef.current = true; setDadosAtuais(dadosAtuais.filter(c => c.id !== idParaDeletar)); };

    // Tab change handlers (Unchanged)
    const handleCategoriaPrincipalChange = (event, novaCategoria) => { if (novaCategoria !== null) { setCategoriaPrincipal(novaCategoria); } };
    const handleSubCategoriaChange = (event, novaSubCategoria) => { if (novaSubCategoria !== null) { setSubCategoria(novaSubCategoria); } };

    // Callback to update areas list after creation in dialog (Unchanged)
    const handleNovaAreaCriadaNoDialogo = (novaArea) => {
        setAreas(prev => [...prev, novaArea]);
        setNotificacao({ aberta: true, mensagem: `Área "${novaArea.name}" criada!`, tipo: 'success' });
    };

    // Callback para Categoria (MODIFICADO)
    // Recebe o objeto completo
    const handleNovaCategoriaCriada = (novaCategoria) => {
        setCategories(prev => [...prev, novaCategoria]); // <-- Adiciona o novo objeto {id, name}
        setNotificacao({ aberta: true, mensagem: `Categoria "${novaCategoria.name}" pronta para uso!`, tipo: 'success' });
    };

    const handleConfirmDeleteCategory = async (categoryIdToDelete) => {
        if (!categoryIdToDelete) return;
    
        try {
            // 1. Chamar a API para deletar
            const res = await fetch(`${process.env.REACT_APP_API_URL}/categories/${categoryIdToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
    
            if (!res.ok) {
                let errorData = { message: `Falha ao excluir. Status: ${res.status}` };
                try { errorData = await res.json(); } catch(e) { /* Ignore */ }
                throw new Error(errorData.message || 'Falha ao excluir categoria.');
            }
    
            // 2. Atualizar o estado local (remover a categoria da lista)
            const deletedCategory = categories.find(c => c.id.toString() === categoryIdToDelete.toString());
            setCategories(prev => prev.filter(c => c.id.toString() !== categoryIdToDelete.toString()));
    
            // 3. "Limpar" as competências que usavam esta categoria
            setTecnicas(prev => prev.map(comp => 
                (comp.categoriaId && comp.categoriaId.toString() === categoryIdToDelete.toString())
                    ? { ...comp, categoriaId: null } 
                    : comp
            ));
            setOutras(prev => prev.map(comp => 
                (comp.categoriaId && comp.categoriaId.toString() === categoryIdToDelete.toString())
                    ? { ...comp, categoriaId: null } 
                    : comp
            ));
    
            // 4. Marcar para auto-save
            isDirtyRef.current = true;
    
            // 5. Fechar modal e notificar
            setDeleteCategoryConfirm({ isOpen: false, category: null });
            setNotificacao({ aberta: true, mensagem: `Categoria "${deletedCategory?.name || ''}" excluída com sucesso!`, tipo: 'success' });
    
        } catch (error) {
            console.error("Erro ao excluir categoria:", error);
            setDeleteCategoryConfirm({ isOpen: false, category: null });
            setNotificacao({ aberta: true, mensagem: `Erro: ${error.message}`, tipo: 'error' });
        }
    };

    const podeAdicionar = dadosAtuais.length < MAX_COMPETENCIAS;

    // Map type props (Unchanged)
    const getTipoProps = (tipo) => {
        switch (tipo) {
            case 'gerenciais': return { Icon: UserCheck, color: theme.palette.managerial };
            case 'tecnicas': return { Icon: ShieldCheck, color: theme.palette.technical };
            case 'outras': return { Icon: Shapes, color: theme.palette.other };
            case 'corporativas': default: return { Icon: Wand2, color: theme.palette.primary };
        }
    };

    // Map Area ID to Name (Unchanged)
    const areaIdToNameMap = useMemo(() => {
        return new Map(areas.map(a => [a.id.toString(), a.name]));
    }, [areas]);

    
    // NOVO: Map Category ID to Name
    const categoryIdToNameMap = useMemo(() => {
        return new Map(categories.map(c => [c.id.toString(), c.name]));
    }, [categories]);

    // REMOVIDO: 'categoriasDisponiveis' useMemo não é mais necessário.


    // --- Main Render (MODIFIED) ---
    if (carregando || isLoadingAreas || isLoadingCategories) { // <-- Verificando os 3 loadings
        return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}><CircularProgress /></Box>);
    }
    
    // Estilo comum para os chips de Area e Categoria
    const chipStyle = {
        mt: 0.5, 
        height: 'auto', 
        '& .MuiChip-label': { py: '2px', fontSize: '0.75rem' }, 
        color: theme.palette.secondary.dark, 
        borderColor: theme.palette.grey[300], 
        cursor: 'default'
    };


    return (
        <PageWrapper>
            <Container maxWidth="lg">
                {/* Header Section (Unchanged) */}
                <HeaderSection>
                     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}> <Typography variant={isMobile ? "h4" : "h2"} component="h1" color="text.primary"> Dicionário de Competências </Typography> <Tooltip title="Ver vídeo explicativo"> <IconButton onClick={() => setIsExplanatoryVideoModalOpen(true)} sx={{ backgroundColor: '#fef9c3', color: '#a16207', '&:hover': { backgroundColor: '#fef08a' } }} > <HelpCircleIcon /> </IconButton> </Tooltip> </Box>
                     <Typography variant="h6" color="text.secondary" sx={{ mt: 1.5, maxWidth: '650px', mx: 'auto' }}> Defina e gerencie os comportamentos e habilidades essenciais para o sucesso na sua organização. </Typography>
                     <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}> <Button variant="outlined" startIcon={<Upload size={18} />} onClick={() => setIsImportModalOpen(true)} > Importar de CSV </Button> </Box>
                </HeaderSection>

                {/* Tab Selectors (Unchanged) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', mb: 2, animation: `${fadeInUp} 0.7s ease-out 0.2s both` }}>
                    <BaseTabSelector value={categoriaPrincipal} exclusive onChange={handleCategoriaPrincipalChange}>
                         <ToggleButton value="comportamentais" sx={{ '&.Mui-selected': { color: 'primary.main', bgcolor: 'primary.light' } }}><Briefcase size={18} style={{ marginRight: 8 }} /> Comportamentais</ToggleButton>
                         <ToggleButton value="tecnicas" sx={{ '&.Mui-selected': { color: 'technical.main', bgcolor: 'technical.light' } }}><Puzzle size={18} style={{ marginRight: 8 }} /> Técnicas</ToggleButton>
                         <ToggleButton value="outras" sx={{ '&.Mui-selected': { color: 'other.main', bgcolor: 'other.light' } }}><Shapes size={18} style={{ marginRight: 8 }} /> Outras</ToggleButton>
                    </BaseTabSelector>
                    
                    {dadosAtuais.length > 0 && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<Trash2 size={18} />}
                            onClick={() => handleAbrirExclusaoMassa(nomeTipoAtual)}
                            sx={{ mt: isMobile ? 2 : 0, ml: 'auto' }}
                        >
                            Remover Todas ({dadosAtuais.length})
                        </Button>
                    )}
                </Box>
                <Collapse in={categoriaPrincipal === 'comportamentais'}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, mt: 1}}>
                         <BaseTabSelector value={subCategoria} exclusive onChange={handleSubCategoriaChange}>
                             <ToggleButton value="corporativas" sx={{ '&.Mui-selected': { color: 'primary.main', bgcolor: 'primary.light' } }}><Building2 size={18} style={{ marginRight: 8 }} /> Corporativas</ToggleButton>
                             <ToggleButton value="gerenciais" sx={{ '&.Mui-selected': { color: 'managerial.main', bgcolor: 'managerial.light' } }}><Users size={18} style={{ marginRight: 8 }} /> Gerenciais</ToggleButton>
                         </BaseTabSelector>
                    </Box>
                </Collapse>

                {/* --- Competency Grid (MODIFIED) --- */}
                {/* Agora usa 'categoriaId' e 'categoryIdToNameMap' */}
                <CardsGrid>
                    {podeAdicionar && (
                        <AddCard onClick={() => abrirDialogo(null)}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Plus size={26} style={{ marginBottom: 8 }} />
                                <Typography fontWeight="600">Adicionar Competência</Typography>
                            </Box>
                        </AddCard>
                    )}
                    {dadosAtuais.map((comp, index) => {
                        const { Icon, color } = getTipoProps(tipoAtual);
                        const iconSize = getIconSize(comp.nome);
                        const iconBoxSize = iconSize + 12;

                        // Lógica para buscar Area e Categoria
                        const isTechnicalOrOther = tipoAtual === 'tecnicas' || tipoAtual === 'outras';
                        
                        const areaName = isTechnicalOrOther && comp.areaId
                            ? areaIdToNameMap.get(comp.areaId.toString())
                            : null;
                            
                        const categoriaName = isTechnicalOrOther && comp.categoriaId // <-- USA categoriaId
                            ? categoryIdToNameMap.get(comp.categoriaId.toString()) // <-- USA categoryIdToNameMap
                            : null;

                        return (
                            <CompetencyCard key={comp.id || index} index={index} tipo={tipoAtual}>
                                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                        <CardIconWrapper sx={{ width: iconBoxSize, height: iconBoxSize, backgroundColor: color.light, color: color.main, mt: 0.5 }}>
                                            <Icon size={iconSize} />
                                        </CardIconWrapper>
                                         <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" component="h3" sx={{ fontWeight: '600', color: 'text.primary', lineHeight: 1.35, wordBreak: 'break-word' }}>
                                                {comp.nome}
                                            </Typography>
                                            
                                            {(areaName || categoriaName) && (
                                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                                                    {areaName && (
    <Chip 
        icon={<Building size={14} />} 
        label={areaName} 
        size="small" 
        variant="outlined"
        sx={{
            ...chipStyle,
            maxWidth: '100%',
            '& .MuiChip-label': { 
                ...chipStyle['& .MuiChip-label'],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                maxWidth: '150px'
            }
        }}
    />
)}
{categoriaName && (
    <Chip 
        icon={<Shapes size={14} />} 
        label={categoriaName} 
        size="small" 
        variant="outlined"
        sx={{
            ...chipStyle, 
            borderColor: color.main, 
            color: color.dark || color.main,
            maxWidth: '100%',
            '& .MuiChip-label': { 
                ...chipStyle['& .MuiChip-label'],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                maxWidth: '150px'
            }
        }}
    />
)}

                                                </Stack>
                                            )}
                                         </Box>
                                    </Box>
                                    <Box sx={{ flexGrow: 1, pl: `calc(${iconBoxSize}px + ${theme.spacing(1.5)})` }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                                            {comp.descricao}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, mt: 'auto' }}>
                                    <Tooltip title="Editar"><IconButton size="small" sx={{ p: 0.5 }} onClick={() => abrirDialogo(comp)}><FilePenLine size={16} /></IconButton></Tooltip>
                                    <Tooltip title="Remover"><IconButton size="small" sx={{ p: 0.5 }} onClick={() => deletarCompetencia(comp.id)}><Trash2 size={16} /></IconButton></Tooltip>
                                </Box>
                            </CompetencyCard>
                        );
                    })}
                </CardsGrid>
            </Container>

            {/* Modals (MODIFIED) */}
            <ModalImportacao open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onFileSelect={processCsvFile} />
             <ConfirmCreateAreasDialog
                 open={confirmCreateAreasData.open}
                 areasToCreate={confirmCreateAreasData.areasInfo}
                 onConfirm={(areasWithColors) => handleConfirmCreateAreas(areasWithColors)}
                 onClose={() => handleConfirmCreateAreas(null)}
             />
            <DialogoCompetencia
                 aberto={dialogoAberto} onClose={fecharDialogo} onSave={handleSalvarCompetencia} competencia={competenciaEmEdicao}
                 tipoAtual={tipoAtual} nomeTipo={nomeTipoAtual} 
                 areasDisponiveis={areas} 
                 onNovaAreaCriada={handleNovaAreaCriadaNoDialogo}
                 categoriasDisponiveis={categories} 
                 onNovaCategoriaCriada={handleNovaCategoriaCriada}
                onDeleteCategory={(category) => setDeleteCategoryConfirm({ isOpen: true, category })} 
            />
             <ModalExclusaoMassa
                 open={isMassDeleteModalOpen}
                 onClose={handleFecharModalExclusao}
                 onConfirm={handleConfirmarExclusaoMassa}
                 tipoCompetencia={tipoParaExcluir}
                 isDeleting={isDeletingMass}
             />

             <Dialog 
                open={deleteCategoryConfirm.isOpen} 
                onClose={() => setDeleteCategoryConfirm({ isOpen: false, category: null })}
                sx={{ zIndex: 3000 }} // Garante que fica sobre o DialogoCompetencia
            >
                <DialogTitle sx={{ fontWeight: '600' }}>Excluir Categoria?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir a categoria: <strong>"{deleteCategoryConfirm.category?.name}"</strong>?
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta ação **não pode ser desfeita**. As competências que usam esta categoria ficarão "Sem Categoria".
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCategoryConfirm({ isOpen: false, category: null })}>Cancelar</Button>
                    <Button 
                        onClick={() => handleConfirmDeleteCategory(deleteCategoryConfirm.category?.id)} 
                        color="error" 
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>

            <AutoSaveFeedback status={autoSaveStatus} />
            <Snackbar open={notificacao.aberta} autoHideDuration={6000} onClose={() => setNotificacao({ ...notificacao, aberta: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotificacao({ ...notificacao, aberta: false })} severity={notificacao.tipo} sx={{ width: '100%', boxShadow: 3 }} iconMapping={{ success: <CheckCircle2 fontSize="inherit" />, error: <AlertTriangle fontSize="inherit" />, warning: <AlertTriangle fontSize="inherit" /> }}>
                    {notificacao.mensagem}
                </Alert>
            </Snackbar>
            <ExplanatoryVideoModal isOpen={isExplanatoryVideoModalOpen} onClose={() => setIsExplanatoryVideoModalOpen(false)} />
        </PageWrapper>
    );
};

// --- Default Export ---
export default function CorporateCompetenciesPage() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles styles={{ '@import': "url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap')" }} />
            <PaginaCompetenciasProfissional />
        </ThemeProvider>
    );
}